import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  FiEdit3,
  FiCopy,
  FiArrowLeft,
  FiCheck,
  FiSend,
  FiEye,
  FiInfo,
} from "react-icons/fi";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import { fetchEmailTemplate, updateEmailTemplate, sendTestEmail } from "../api/contacts";

// Helper function to resolve Spintax {Hi|Hello|Hey}
function resolveSpintaxPreview(text) {
  if (!text) return "";
  let result = text;
  const pattern = /\{([^{}]+)\}/g;
  let matches = listMatches(pattern, result);
  let attempts = 0;

  while (matches.length > 0 && attempts < 10) {
    attempts++;
    let replaced = false;
    result = result.replace(/\{([^{}]+)\}/g, (match, p1) => {
      if (p1.includes("|")) {
        replaced = true;
        const options = p1.split("|");
        return options[0]; // pick first option for stable live preview
      }
      return match;
    });
    if (!replaced) break;
  }
  return result;
}

function listMatches(pattern, str) {
  const matches = [];
  let m;
  while ((m = pattern.exec(str)) !== null) {
    if (m[1] && m[1].includes("|")) matches.push(m);
  }
  return matches;
}

// Helper function to replace placeholders with sample data
function previewWithSampleData(text) {
  const sampleData = {
    name: "John Smith",
    company_name: "Oris Dental Center",
    email: "odental657@gmail.com",
    phone: "043809990",
    category: "Dentists",
    area: "Jumeirah",
    address: "Umm Sequeim 3, Jumeirah Road, Opposite Burj Al Arab",
    website: "https://www.orisdentalcenter.ae/",
    services_offered: "Dental Care, Cosmetic Dentistry, Orthodontics",
    description: "Leading dental clinic offering top-tier healthcare and smile makeovers in Dubai.",
  };

  if (!text) return "";

  let result = resolveSpintaxPreview(text);
  Object.keys(sampleData).forEach((key) => {
    const regex = new RegExp(`\\{${key}\\}`, "g");
    result = result.replace(regex, sampleData[key]);
  });

  return result;
}

export default function EditTemplate() {
  const { id } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("preview"); // 'preview', 'raw', or 'html'

  // Test Email States
  const [testEmailAddr, setTestEmailAddr] = useState(user?.email || "");
  const [sendingTest, setSendingTest] = useState(false);
  const [savedTestEmails, setSavedTestEmails] = useState([]);

  // Load saved test emails
  useEffect(() => {
    const stored = localStorage.getItem("saved_test_emails");
    let emails = [user?.email].filter(Boolean);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          emails = parsed;
        }
      } catch (e) {}
    }
    setSavedTestEmails(emails);
    if (emails.length > 0) {
      setTestEmailAddr(emails[0]);
    }
  }, [user]);

  // Load Template Data
  const loadTemplate = useCallback(async () => {
    if (!token?.access || !id) return;
    setLoading(true);
    try {
      const data = await fetchEmailTemplate(token.access, id);
      setName(data.name || "");
      setSubject(data.subject || "");
      setBody(data.body || "");
    } catch (err) {
      console.error("Failed to load template:", err.message);
      toast.error(err.message || "Failed to load template");
      navigate("/dashboard/templates/list");
    } finally {
      setLoading(false);
    }
  }, [token, id, navigate]);

  useEffect(() => {
    loadTemplate();
  }, [loadTemplate]);

  // Available placeholders
  const placeholders = [
    { key: "name", label: "Contact Name" },
    { key: "company_name", label: "Company Name" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
    { key: "category", label: "Category" },
    { key: "area", label: "Area" },
    { key: "address", label: "Address" },
    { key: "website", label: "Website" },
    { key: "services_offered", label: "Services Offered" },
    { key: "description", label: "Description" },
  ];

  const insertPlaceholder = (placeholder) => {
    setBody((prev) => prev + `{${placeholder}}`);
    toast.success(`Inserted {${placeholder}} into body`);
  };

  const insertSubjectPlaceholder = (placeholder) => {
    setSubject((prev) => prev + `{${placeholder}}`);
    toast.success(`Inserted {${placeholder}} into subject`);
  };

  const copyPlaceholder = (placeholder) => {
    navigator.clipboard.writeText(`{${placeholder}}`);
    toast.success(`Copied {${placeholder}}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token?.access) return;

    if (!name.trim()) {
      toast.error("Template name is required");
      return;
    }
    if (!subject.trim()) {
      toast.error("Subject is required");
      return;
    }
    if (!body.trim()) {
      toast.error("Email body is required");
      return;
    }

    setSaving(true);
    try {
      await updateEmailTemplate(token.access, id, {
        name: name.trim(),
        subject: subject.trim(),
        body: body.trim(),
      });

      toast.success("Template updated successfully! 🎉");
      navigate("/dashboard/templates/list");
    } catch (err) {
      console.error("Error updating template:", err.message);
      toast.error(err.message || "Error updating template");
    } finally {
      setSaving(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmailAddr) {
      toast.error("Please enter a test email address!");
      return;
    }
    setSendingTest(true);
    try {
      const res = await sendTestEmail(
        token.access,
        testEmailAddr,
        id,
        subject,
        body
      );
      toast.success(res.message || `Test email sent to ${testEmailAddr}! 🎉`);
    } catch (err) {
      toast.error(err.message || "Failed to send test email");
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return (
      <div className="p-12 flex flex-col items-center justify-center text-gray-500">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-r-transparent mb-3"></div>
        <p className="text-sm font-medium">Loading template details...</p>
      </div>
    );
  }

  const previewSubject = previewWithSampleData(subject);
  const previewBody = previewWithSampleData(body);
  const formattedPreviewBody = previewBody.replace(/\n/g, "<br>");

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Top Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <Link
              to="/dashboard/templates/list"
              className="p-2 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition"
              title="Back to templates"
            >
              <FiArrowLeft size={20} />
            </Link>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <FiEdit3 className="text-blue-600" /> Edit Email Template
            </h2>
          </div>
          <p className="text-gray-500 text-sm mt-1 ml-11">
            Update template content, customize dynamic tags, and preview live output
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Link
            to="/dashboard/templates/list"
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition"
          >
            Cancel
          </Link>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-5 py-2 rounded-lg flex items-center gap-2 transition disabled:opacity-50 shadow-sm"
          >
            <FiCheck size={18} />
            {saving ? "Saving Changes..." : "Save Changes"}
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left Column: Form */}
        <div className="space-y-6">
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-4 pb-2 border-b">
              Template Information
            </h3>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Template Name */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                  Template Name *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Cold Outreach - Dentist Offer"
                  className="w-full border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  required
                />
              </div>

              {/* Subject */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Email Subject *
                  </label>
                  <span className="text-xs text-gray-400">Supports Spintax & Placeholders</span>
                </div>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="e.g., Quick question regarding {company_name}"
                  className="w-full border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  required
                />
              </div>

              {/* Placeholders Toolbar */}
              <div className="pt-2">
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                  Insert Dynamic Placeholders:
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {placeholders.map((p) => (
                    <div
                      key={p.key}
                      className="inline-flex items-center rounded-md bg-blue-50 border border-blue-200 text-blue-700 text-xs font-medium overflow-hidden shadow-2xs hover:bg-blue-100 transition"
                    >
                      <button
                        type="button"
                        onClick={() => insertPlaceholder(p.key)}
                        className="px-2 py-1 hover:bg-blue-200/60 transition"
                        title={`Insert {${p.key}} into body`}
                      >
                        +{p.label}
                      </button>
                      <button
                        type="button"
                        onClick={() => insertSubjectPlaceholder(p.key)}
                        className="px-1.5 py-1 border-l border-blue-200 hover:bg-blue-200 transition text-[10px] text-blue-600 font-semibold"
                        title={`Insert {${p.key}} into subject`}
                      >
                        Subj
                      </button>
                      <button
                        type="button"
                        onClick={() => copyPlaceholder(p.key)}
                        className="px-1.5 py-1 border-l border-blue-200 hover:bg-blue-200 transition text-blue-500"
                        title="Copy tag"
                      >
                        <FiCopy size={11} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Body */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Email Body Content *
                  </label>
                  <span className="text-xs text-gray-400">Plain text / HTML line breaks</span>
                </div>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={13}
                  placeholder={`Hi {name},\n\nI was looking into {company_name} and noticed your work in {services_offered}...\n\nBest regards,\nHamza`}
                  className="w-full border border-gray-300 rounded-lg p-3.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition leading-relaxed"
                  required
                />
              </div>

              {/* Spintax Helper Card */}
              <div className="bg-amber-50/70 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 flex items-start gap-2">
                <FiInfo className="mt-0.5 shrink-0 text-amber-600" size={16} />
                <div>
                  <span className="font-semibold">Spintax Syntax Supported:</span> Use syntax like{" "}
                  <code className="bg-amber-100 px-1 py-0.5 rounded font-mono text-amber-950">
                    {"{Hi|Hello|Hey}"}
                  </code>{" "}
                  to randomly alternate greeting or phrases across outgoing emails.
                </div>
              </div>

              <div className="pt-2 flex items-center justify-end gap-3">
                <button
                  type="submit"
                  disabled={saving}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2 transition disabled:opacity-50 shadow-sm"
                >
                  <FiCheck size={18} />
                  {saving ? "Saving Changes..." : "Save Template"}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Right Column: Live Preview & Send Test */}
        <div className="space-y-6">
          {/* Live Preview Card */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col">
            <div className="px-6 py-4 bg-gray-50/80 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <FiEye className="text-blue-600" /> Live Interactive Preview
              </h3>
              <div className="flex bg-gray-200/70 p-0.5 rounded-lg text-xs font-medium">
                <button
                  onClick={() => setActiveTab("preview")}
                  className={`px-3 py-1 rounded-md transition ${
                    activeTab === "preview"
                      ? "bg-white text-blue-600 shadow-xs font-semibold"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  Sample Preview
                </button>
                <button
                  onClick={() => setActiveTab("raw")}
                  className={`px-3 py-1 rounded-md transition ${
                    activeTab === "raw"
                      ? "bg-white text-blue-600 shadow-xs font-semibold"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  Raw Tags
                </button>
                <button
                  onClick={() => setActiveTab("html")}
                  className={`px-3 py-1 rounded-md transition ${
                    activeTab === "html"
                      ? "bg-white text-blue-600 shadow-xs font-semibold"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  HTML View
                </button>
              </div>
            </div>

            {/* Email Client Mockup */}
            <div className="p-5 flex-1 flex flex-col bg-gray-50/30">
              <div className="bg-white border border-gray-200 rounded-lg shadow-xs overflow-hidden">
                {/* Email Client Header */}
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 space-y-2 text-xs">
                  <div className="flex items-center text-gray-600">
                    <span className="w-16 font-semibold text-gray-500">To:</span>
                    <span className="text-gray-900 font-medium">
                      {activeTab === "preview"
                        ? "John Smith <odental657@gmail.com>"
                        : "{name} <{email}>"}
                    </span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <span className="w-16 font-semibold text-gray-500">Company:</span>
                    <span className="text-gray-900">
                      {activeTab === "preview" ? "Oris Dental Center (Dentists - Jumeirah)" : "{company_name} ({category})"}
                    </span>
                  </div>
                  <div className="flex items-start text-gray-600">
                    <span className="w-16 font-semibold text-gray-500 mt-0.5">Subject:</span>
                    <span className="text-gray-900 font-semibold flex-1">
                      {activeTab === "preview"
                        ? previewSubject || <span className="text-gray-400 font-normal italic">No subject entered</span>
                        : subject || <span className="text-gray-400 font-normal italic">No subject entered</span>}
                    </span>
                  </div>
                </div>

                {/* Email Client Body */}
                <div className="p-5 min-h-[260px] text-sm text-gray-800 leading-relaxed font-sans">
                  {activeTab === "preview" ? (
                    <div>
                      {body ? (
                        <div
                          dangerouslySetBodyInnerHTML={{
                            __html: formattedPreviewBody,
                          }}
                        />
                      ) : (
                        <p className="text-gray-400 italic">Enter template body on the left to see live preview...</p>
                      )}

                      {/* Unsubscribe Footer Preview */}
                      <div className="mt-8 pt-4 border-t border-gray-100 text-[11px] text-gray-400">
                        If you no longer wish to receive these emails,{" "}
                        <span className="text-gray-400 underline cursor-pointer">
                          unsubscribe here
                        </span>
                        .
                      </div>
                    </div>
                  ) : activeTab === "raw" ? (
                    <pre className="font-mono text-xs text-gray-700 whitespace-pre-wrap leading-relaxed">
                      {body || <span className="text-gray-400 italic">No template body entered</span>}
                    </pre>
                  ) : (
                    <pre className="font-mono text-xs text-blue-900 bg-blue-50/50 p-3 rounded border border-blue-100 whitespace-pre-wrap overflow-x-auto">
                      {`<html>\n  <body style="font-family:Arial,sans-serif;line-height:1.6;color:#333;margin:0;padding:10px;">\n    <div>${formattedPreviewBody}</div>\n    <p style="font-size:11px;color:#999;margin-top:24px;border-top:1px solid #f0f0f0;padding-top:12px;">\n      If you no longer wish to receive these emails, <a href="...">unsubscribe here</a>.\n    </p>\n  </body>\n</html>`}
                    </pre>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Send Test Email Card */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <FiSend className="text-blue-600" /> Send Test Email
            </h3>
            <p className="text-xs text-gray-500 mb-4">
              Send a test email with this template&apos;s current edits to your inbox before saving.
            </p>

            <div className="flex flex-col sm:flex-row gap-2">
              {savedTestEmails.length > 1 ? (
                <select
                  value={testEmailAddr}
                  onChange={(e) => setTestEmailAddr(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 flex-1 bg-white"
                >
                  {savedTestEmails.map((em) => (
                    <option key={em} value={em}>
                      {em}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="email"
                  value={testEmailAddr}
                  onChange={(e) => setTestEmailAddr(e.target.value)}
                  placeholder="your-email@gmail.com"
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 flex-1"
                />
              )}

              <button
                type="button"
                onClick={handleSendTestEmail}
                disabled={sendingTest}
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-4 py-2.5 rounded-lg flex items-center justify-center gap-1.5 transition disabled:opacity-50 shadow-xs shrink-0"
              >
                <FiSend size={14} />
                {sendingTest ? "Sending..." : "Send Test"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
